import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import { otpService } from '../services/otpService';
import { getErrorMessage } from '../utils/errorHandler';
import { storage } from '../services/storage';
import { registerDevice, setupTokenRefreshListener, clearDeviceRegistration } from '../services/pushNotifications';
import { wixCommentsApi } from '../services/api';

/**
 * Clear all locally cached data on logout
 * - IndexedDB (via storage.clearAll)
 * - LocalStorage (bonndle: prefixed items)
 * - Service Worker caches
 */
const clearAllCachedData = async () => {
  try {
    // 1. Clear IndexedDB
    await storage.clearAll();
    console.log('Cleared IndexedDB');
  } catch (e) {
    console.warn('Failed to clear IndexedDB:', e);
  }

  try {
    // 2. Clear localStorage items with bonndle prefix
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('bonndle') || key.startsWith('auth_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    console.log('Cleared localStorage:', keysToRemove.length, 'items');
  } catch (e) {
    console.warn('Failed to clear localStorage:', e);
  }

  try {
    // 3. Clear Service Worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      console.log('Cleared caches:', cacheNames);
    }
  } catch (e) {
    console.warn('Failed to clear caches:', e);
  }

  try {
    // 4. Unregister service workers to force fresh load
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
      console.log('Unregistered service workers:', registrations.length);
    }
  } catch (e) {
    console.warn('Failed to unregister service workers:', e);
  }
};

const AuthContext = createContext(null);

// Auth step constants
export const AUTH_STEPS = {
  WELCOME: 'welcome',
  METHODS: 'methods',
  DETAILS: 'details',
  LOGIN_CONTACT: 'login_contact',
  VERIFICATION: 'verification',
  PIN: 'pin',
  PIN_CREATE: 'pin_create',
  SUCCESS: 'success',
};

// Auth mode constants
export const AUTH_MODE = {
  SIGNUP: 'signup',
  LOGIN: 'login',
};

/**
 * Check localStorage for existing auth
 */
const getInitialAuthState = () => {
  try {
    return bonndleAuthApi.isAuthenticated();
  } catch {
    return false;
  }
};

/**
 * Get initial user from storage
 */
const getInitialUser = () => {
  try {
    return bonndleAuthApi.getCurrentUser();
  } catch {
    return null;
  }
};

/**
 * Get cached rewards balance from localStorage
 * This allows showing the last known balance immediately without loading
 */
const getCachedRewardsBalance = () => {
  try {
    const cached = localStorage.getItem('bonndle:rewards_cache');
    if (cached) {
      const data = JSON.parse(cached);
      return {
        balance: data.balance || 0,
        pointsEarnedToday: data.pointsEarnedToday || 0,
        lastUpdated: data.lastUpdated || null,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { balance: 0, pointsEarnedToday: 0, lastUpdated: null };
};

/**
 * Save rewards balance to localStorage cache
 */
const cacheRewardsBalance = (balance, pointsEarnedToday = 0) => {
  try {
    localStorage.setItem('bonndle:rewards_cache', JSON.stringify({
      balance,
      pointsEarnedToday,
      lastUpdated: Date.now(),
    }));
  } catch (e) {
    console.warn('Failed to cache rewards balance:', e);
  }
};

/**
 * Get initial userProfile from sessionStorage
 * This ensures data persists across page navigations/refreshes during signup
 */
const getInitialUserProfile = () => {
  try {
    const stored = sessionStorage.getItem('bonndle:signup_profile');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    country: 'NG',
    referralCode: '',
    boldTag: '',
  };
};

/**
 * Check if profile is complete
 */
const getInitialProfileComplete = () => {
  try {
    return localStorage.getItem('bonndle:profile_complete') === 'true';
  } catch {
    return false;
  }
};

export function AuthProvider({ children }) {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(getInitialAuthState);
  const [isGuestMode, setIsGuestMode] = useState(() => localStorage.getItem('bonndle:guest_mode') === 'true');
  const [user, setUser] = useState(getInitialUser);
  const [profileComplete, setProfileComplete] = useState(getInitialProfileComplete);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGuestPrompt, setShowGuestPrompt] = useState(false);

  // Flow state
  const [authMode, setAuthMode] = useState(AUTH_MODE.LOGIN);
  const [authStep, setAuthStep] = useState(AUTH_STEPS.WELCOME);
  const [signupMethod, setSignupMethod] = useState(null); // 'phone' | 'email' | 'google' | 'apple'

  // Form data - persisted to sessionStorage during signup flow
  const [userProfile, setUserProfileState] = useState(getInitialUserProfile);

  // Wrapper to persist userProfile changes to sessionStorage
  const setUserProfile = useCallback((updater) => {
    setUserProfileState((prev) => {
      const newProfile = typeof updater === 'function' ? updater(prev) : updater;
      // Persist to sessionStorage for navigation/refresh resilience
      try {
        sessionStorage.setItem('bonndle:signup_profile', JSON.stringify(newProfile));
      } catch (e) {
        console.warn('Failed to persist userProfile:', e);
      }
      return newProfile;
    });
  }, []);

  // OTP and PIN input states
  const [otpValue, setOtpValue] = useState(['', '', '', '']);
  const [pinValue, setPinValue] = useState(['', '', '', '']);

  // Rewards and balance state - initialize with cached values for instant display
  const cachedRewards = getCachedRewardsBalance();
  const [rewardsData, setRewardsData] = useState({
    balance: cachedRewards.balance,
    pointsEarnedToday: cachedRewards.pointsEarnedToday,
    transactions: [],
    isLoading: false,
    isSilentRefreshing: false, // Background refresh without UI loading state
  });

  // Ref to store token refresh cleanup function
  const tokenRefreshCleanupRef = useRef(null);

  // Set up FCM token refresh listener when logged in
  useEffect(() => {
    if (isLoggedIn) {
      // Set up token refresh listener
      setupTokenRefreshListener().then((cleanup) => {
        tokenRefreshCleanupRef.current = cleanup;
      });
    }

    return () => {
      // Clean up token refresh listener on unmount or logout
      if (tokenRefreshCleanupRef.current) {
        tokenRefreshCleanupRef.current();
        tokenRefreshCleanupRef.current = null;
      }
    };
  }, [isLoggedIn]);

  // Listen for auth expiration events
  useEffect(() => {
    const handleAuthExpired = () => {
      setIsLoggedIn(false);
      setUser(null);
      setAuthStep(AUTH_STEPS.WELCOME);
      setError('Your session has expired. Please log in again.');
    };

    window.addEventListener('bonndle:auth:expired', handleAuthExpired);
    return () => window.removeEventListener('bonndle:auth:expired', handleAuthExpired);
  }, []);

  // Clear error helper
  const clearError = useCallback(() => setError(null), []);

  /**
   * Fetch user profile and rewards data after login
   * @param {boolean} silent - If true, fetch without showing loading state (background refresh)
   */
  const fetchUserData = useCallback(async (silent = false) => {
    // Only show loading if not silent and no cached balance
    if (!silent) {
      const cached = getCachedRewardsBalance();
      // Only show loading if we have no cached balance
      if (cached.balance === 0) {
        setRewardsData((prev) => ({ ...prev, isLoading: true }));
      } else {
        setRewardsData((prev) => ({ ...prev, isSilentRefreshing: true }));
      }
    } else {
      setRewardsData((prev) => ({ ...prev, isSilentRefreshing: true }));
    }

    try {
      // Fetch profile and rewards in parallel
      const [profile, rewardHistory] = await Promise.all([
        bonndleAuthApi.getProfile().catch((err) => {
          console.warn('Failed to fetch profile:', err);
          return null;
        }),
        bonndleAuthApi.getRewardHistory(1, 10).catch((err) => {
          console.warn('Failed to fetch rewards:', err);
          return { transactions: [], pointsEarnedToday: 0 };
        }),
      ]);

      // Update user profile if fetched successfully
      if (profile) {
        setUser(profile);
      }

      // Calculate new balance
      const newBalance = profile?.available_bold_credits || profile?.bold_credits || profile?.boldCredits || 0;
      const newPointsEarnedToday = rewardHistory.pointsEarnedToday || 0;

      // Cache the new balance for instant display on next load
      cacheRewardsBalance(newBalance, newPointsEarnedToday);

      // Update rewards data - API uses 'available_bold_credits'
      setRewardsData({
        balance: newBalance,
        pointsEarnedToday: newPointsEarnedToday,
        transactions: rewardHistory.transactions || [],
        isLoading: false,
        isSilentRefreshing: false,
      });

      return { profile, rewardHistory };
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setRewardsData((prev) => ({ ...prev, isLoading: false, isSilentRefreshing: false }));
      throw err;
    }
  }, []);

  /**
   * Refresh rewards data only
   * @param {boolean} silent - If true, refresh without showing loading state
   */
  const refreshRewards = useCallback(async (silent = false) => {
    if (!silent) {
      setRewardsData((prev) => ({ ...prev, isLoading: true }));
    } else {
      setRewardsData((prev) => ({ ...prev, isSilentRefreshing: true }));
    }

    try {
      // Fetch both profile (for balance) and reward history in parallel
      const [profile, rewardHistory] = await Promise.all([
        bonndleAuthApi.getProfile().catch((err) => {
          console.warn('Failed to fetch profile for balance:', err);
          return null;
        }),
        bonndleAuthApi.getRewardHistory(1, 10),
      ]);

      const newBalance = profile?.available_bold_credits || profile?.bold_credits || profile?.boldCredits || rewardsData.balance;
      const newPointsEarnedToday = rewardHistory.pointsEarnedToday || 0;

      // Cache the updated balance
      cacheRewardsBalance(newBalance, newPointsEarnedToday);

      setRewardsData((prev) => ({
        ...prev,
        balance: newBalance,
        pointsEarnedToday: newPointsEarnedToday,
        transactions: rewardHistory.transactions || [],
        isLoading: false,
        isSilentRefreshing: false,
      }));

      return rewardHistory;
    } catch (err) {
      console.error('Failed to refresh rewards:', err);
      setRewardsData((prev) => ({ ...prev, isLoading: false, isSilentRefreshing: false }));
      throw err;
    }
  }, [rewardsData.balance]);

  /**
   * Silently refresh balance in background (convenience wrapper)
   * Called when user navigates to home screen to get latest balance
   */
  const silentRefreshBalance = useCallback(() => {
    // Don't refresh if already refreshing
    if (rewardsData.isSilentRefreshing) return;

    // Refresh in background without showing loading state
    refreshRewards(true).catch((err) => {
      console.warn('Silent balance refresh failed:', err);
    });
  }, [refreshRewards, rewardsData.isSilentRefreshing]);

  // Start onboarding with selected mode
  const startOnboarding = useCallback((mode) => {
    setAuthMode(mode);
    clearError();
    if (mode === AUTH_MODE.LOGIN) {
      setAuthStep(AUTH_STEPS.LOGIN_CONTACT);
    } else {
      setAuthStep(AUTH_STEPS.METHODS);
    }
  }, [clearError]);

  /**
   * Check if contact (email/phone) exists
   */
  const checkContact = useCallback(async (type, value) => {
    setIsLoading(true);
    clearError();
    try {
      const result = type === 'phone'
        ? await bonndleAuthApi.checkPhone(value)
        : await bonndleAuthApi.checkEmail(value);
      return result;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Handle auth method selection (signup)
   */
  const handleAuthMethod = useCallback(async (method, contact, nameData) => {
    setSignupMethod(method);
    clearError();

    if (method === 'google' || method === 'apple') {
      // OAuth will be handled by separate handlers
      return;
    }

    // Phone or email signup
    const updates = {};
    if (nameData?.firstName) updates.firstName = nameData.firstName;
    if (nameData?.lastName) updates.lastName = nameData.lastName;
    if (contact) {
      if (method === 'phone') {
        updates.phone = contact;
      } else {
        updates.email = contact;
      }
    }

    setUserProfile((prev) => ({ ...prev, ...updates }));

    // For signup, send OTP and go to verification
    if (contact) {
      setIsLoading(true);
      try {
        if (method === 'phone') {
          // Use Firebase/Termii OTP service for phone (matches Flutter app)
          await otpService.sendOtp(contact, 'recaptcha-container');
          console.log('[AuthContext] Phone OTP sent via OTP service');
        } else {
          // Use backend API for email OTP
          await bonndleAuthApi.sendOtp({
            email: contact,
          });
        }
        setAuthStep(AUTH_STEPS.VERIFICATION);
      } catch (err) {
        const message = getErrorMessage(err);
        setError(message);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Go to details if no contact provided
      setAuthStep(AUTH_STEPS.DETAILS);
    }
  }, [clearError]);

  /**
   * Handle Google Sign-In
   */
  const handleGoogleSignIn = useCallback(async (idToken) => {
    setIsLoading(true);
    clearError();
    try {
      const data = await bonndleAuthApi.googleSignIn(idToken);
      setUser(data.user);

      // Check if PIN needs to be created (new user)
      if (data.requires_pin_creation || data.is_new_user) {
        setAuthStep(AUTH_STEPS.PIN_CREATE);
      } else {
        // Register device for push notifications (non-blocking)
        registerDevice().catch((err) => {
          console.warn('Failed to register device after Google sign-in:', err);
        });
        setIsLoggedIn(true);
        setAuthStep(AUTH_STEPS.SUCCESS);

        // Dispatch event so other contexts (e.g., AppStateContext) can refresh their data
        window.dispatchEvent(new CustomEvent('bonndle:user:login'));
      }
      return data;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Handle Apple Sign-In
   */
  const handleAppleSignIn = useCallback(async (identityToken) => {
    setIsLoading(true);
    clearError();
    try {
      const data = await bonndleAuthApi.appleSignIn(identityToken);
      setUser(data.user);

      if (data.requires_pin_creation || data.is_new_user) {
        setAuthStep(AUTH_STEPS.PIN_CREATE);
      } else {
        // Register device for push notifications (non-blocking)
        registerDevice().catch((err) => {
          console.warn('Failed to register device after Apple sign-in:', err);
        });
        setIsLoggedIn(true);
        setAuthStep(AUTH_STEPS.SUCCESS);

        // Dispatch event so other contexts (e.g., AppStateContext) can refresh their data
        window.dispatchEvent(new CustomEvent('bonndle:user:login'));
      }
      return data;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Submit login contact (phone/email)
   */
  const submitLoginContact = useCallback(async (contact, method) => {
    setIsLoading(true);
    clearError();

    const contactMethod = method || signupMethod;
    setSignupMethod(contactMethod);

    try {
      // Check if contact exists
      if (contactMethod === 'phone' || !contact.includes('@')) {
        const result = await bonndleAuthApi.checkPhone(contact);
        // API returns { numberExists: true/false } - check if user exists
        if (!result.numberExists) {
          throw new Error('No account found with this phone number. Please sign up first.');
        }
        setUserProfile((prev) => ({ ...prev, phone: contact }));
      } else {
        // For email login, we need to check if the email exists
        // The /check-email API returns an error when email already exists (for signup validation)
        // and a success response with "available" when email is not registered
        let emailExists = false;
        try {
          const result = await bonndleAuthApi.checkEmail(contact);
          // If we get here with "available" message, email is NOT registered
          if (result.message && result.message.toLowerCase().includes('available')) {
            emailExists = false;
          } else {
            // Got a success response without "available" - email might exist
            emailExists = true;
          }
        } catch (checkError) {
          // If the error contains "already exists" or "already registered",
          // that means the user EXISTS - which is what we want for LOGIN
          const errorMsg = checkError.message?.toLowerCase() || '';
          if (errorMsg.includes('already exists') || errorMsg.includes('already registered') || errorMsg.includes('email exists')) {
            // User exists - this is the success case for login
            emailExists = true;
          } else {
            // Some other error - re-throw it
            throw checkError;
          }
        }

        if (!emailExists) {
          throw new Error('No account found with this email. Please sign up first.');
        }
        setUserProfile((prev) => ({ ...prev, email: contact }));
      }

      // Go to PIN verification
      setAuthStep(AUTH_STEPS.PIN);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [signupMethod, clearError]);

  /**
   * Submit profile details (signup)
   */
  const submitDetails = useCallback(async (details) => {
    setIsLoading(true);
    clearError();

    try {
      setUserProfile((prev) => ({ ...prev, ...details }));

      // Check BoldTag if provided
      if (details.boldTag) {
        await bonndleAuthApi.checkBoldTag(details.boldTag);
      }

      // Check referral code if provided
      if (details.referralCode) {
        await bonndleAuthApi.checkReferralCode(details.referralCode);
      }

      // Send OTP for verification
      const phone = details.phone || userProfile.phone;
      const email = details.email || userProfile.email;

      if (phone && signupMethod === 'phone') {
        // Use Firebase/Termii OTP service for phone (matches Flutter app)
        await otpService.sendOtp(phone, 'recaptcha-container');
        console.log('[AuthContext] Phone OTP sent via OTP service');
      } else {
        // Use backend API for email OTP
        await bonndleAuthApi.sendOtp({
          email: email || undefined,
          phone: phone || undefined,
        });
      }

      setAuthStep(AUTH_STEPS.VERIFICATION);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, clearError]);

  /**
   * Verify OTP and complete registration
   */
  const verifyAuth = useCallback(async (otp) => {
    setIsLoading(true);
    clearError();

    try {
      // OTP is passed as string or array
      const otpCode = Array.isArray(otp) ? otp.join('') : otp;

      let phoneVerificationToken = null;
      let otpId = null;

      // For phone-based OTP, verify via Firebase/Termii first (matches Flutter app)
      if (userProfile.phone && signupMethod === 'phone') {
        const otpResult = await otpService.verifyOtp(otpCode);
        if (!otpResult.verified) {
          throw new Error('Invalid verification code. Please try again.');
        }
        console.log('[AuthContext] OTP verified via', otpResult.provider);

        // Get Firebase token and OTP ID for backend verification
        phoneVerificationToken = otpResult.firebaseToken;
        otpId = otpResult.otpId;
      }

      // Register user with OTP and verification fields
      const data = await bonndleAuthApi.register({
        ...userProfile,
        otp: otpCode,
        phoneVerificationToken,
        otpId,
      });

      setUser(data.user);

      // Clear OTP session
      otpService.clearSession();

      // Store BoldTag if provided
      if (userProfile.boldTag) {
        try {
          await bonndleAuthApi.storeBoldTag(userProfile.boldTag);
        } catch (e) {
          console.warn('Failed to store BoldTag:', e);
        }
      }

      // Go to PIN creation
      setAuthStep(AUTH_STEPS.PIN_CREATE);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, signupMethod, clearError]);

  /**
   * Create PIN (for new users)
   */
  const createPin = useCallback(async (pin) => {
    setIsLoading(true);
    clearError();

    try {
      const pinCode = Array.isArray(pin) ? pin.join('') : pin;
      await bonndleAuthApi.createPin(pinCode);

      setIsLoggedIn(true);
      setAuthStep(AUTH_STEPS.SUCCESS);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Confirm PIN (for login)
   */
  const confirmPin = useCallback(async (pin) => {
    setIsLoading(true);
    clearError();

    try {
      const pinCode = Array.isArray(pin) ? pin.join('') : pin;

      if (authMode === AUTH_MODE.LOGIN) {
        // Login with PIN - use /login endpoint for both email and phone
        const data = await bonndleAuthApi.login({
          email: userProfile.email || undefined,
          phone: userProfile.phone || undefined,
          pin: pinCode,
        });

        // Set initial user data from login response
        setUser(data.user);

        // Fetch full user profile BEFORE setting isLoggedIn
        // This ensures first_name, last_name, etc. are available when UI renders
        try {
          await fetchUserData();
        } catch (err) {
          // Silently fail - don't prevent login if profile fetch fails
          console.warn('Failed to fetch user data after login:', err);
        }

        // Register device for push notifications (non-blocking)
        registerDevice().catch((err) => {
          console.warn('Failed to register device after login:', err);
        });

        // Only set logged in AFTER profile is loaded
        setIsLoggedIn(true);

        // Dispatch event so other contexts (e.g., AppStateContext) can refresh their data
        window.dispatchEvent(new CustomEvent('bonndle:user:login'));
      } else {
        // Signup: create PIN
        await bonndleAuthApi.createPin(pinCode);

        // Fetch user data after signup BEFORE setting logged in
        try {
          await fetchUserData();
        } catch (err) {
          console.warn('Failed to fetch user data after signup:', err);
        }

        // Register device for push notifications (non-blocking)
        registerDevice().catch((err) => {
          console.warn('Failed to register device after signup:', err);
        });

        setIsLoggedIn(true);
        setAuthStep(AUTH_STEPS.SUCCESS);

        // Dispatch event so other contexts (e.g., AppStateContext) can refresh their data
        window.dispatchEvent(new CustomEvent('bonndle:user:login'));
      }
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [authMode, userProfile, clearError, fetchUserData]);

  /**
   * Forgot PIN - send OTP for reset
   */
  const forgotPin = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      await bonndleAuthApi.sendOtp({
        email: userProfile.email || undefined,
        phone: userProfile.phone || undefined,
      });
      return true;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, clearError]);

  /**
   * Reset PIN with OTP
   */
  const resetPinWithOtp = useCallback(async (otp, newPin) => {
    setIsLoading(true);
    clearError();

    try {
      await bonndleAuthApi.socialResetPin({
        otp: Array.isArray(otp) ? otp.join('') : otp,
        newPin: Array.isArray(newPin) ? newPin.join('') : newPin,
      });
      return true;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Enter app after success screen
   */
  const enterApp = useCallback(() => {
    setIsLoggedIn(true);
  }, []);

  /**
   * Enter guest mode - allows browsing without account
   */
  const enterGuestMode = useCallback(() => {
    setIsGuestMode(true);
    localStorage.setItem('bonndle:guest_mode', 'true');
  }, []);

  /**
   * Exit guest mode - called when user decides to sign up
   */
  const exitGuestMode = useCallback(() => {
    setIsGuestMode(false);
    localStorage.removeItem('bonndle:guest_mode');
  }, []);

  /**
   * Complete user profile
   */
  const completeProfile = useCallback((profileData) => {
    setUserProfile((prev) => ({ ...prev, ...profileData }));
    setProfileComplete(true);
    localStorage.setItem('bonndle:profile_complete', 'true');
  }, []);

  /**
   * Logout - clears all cached data and resets state
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      // Call API logout
      await bonndleAuthApi.logout();
    } catch (e) {
      console.warn('API logout failed:', e);
    }

    try {
      // Clear all locally cached data (IndexedDB, localStorage, caches, service workers)
      await clearAllCachedData();
    } catch (e) {
      console.warn('Failed to clear cached data:', e);
    }

    // Delete test comments created during this session
    try {
      const testComments = JSON.parse(localStorage.getItem('bonndleTestComments') || '[]');
      if (testComments.length > 0) {
        console.log('Cleaning up test comments:', testComments.length);
        for (const { commentId } of testComments) {
          if (commentId) {
            try {
              await wixCommentsApi.deleteComment(commentId);
              console.log('Deleted test comment:', commentId);
            } catch (deleteErr) {
              console.warn('Failed to delete test comment:', commentId, deleteErr);
            }
          }
        }
        localStorage.removeItem('bonndleTestComments');
        console.log('Test comments cleanup complete');
      }
    } catch (e) {
      console.warn('Failed to clean up test comments:', e);
    }

    // Clear device registration data
    clearDeviceRegistration();

    // Clean up token refresh listener
    if (tokenRefreshCleanupRef.current) {
      tokenRefreshCleanupRef.current();
      tokenRefreshCleanupRef.current = null;
    }

    // Reset all state
    setIsLoggedIn(false);
    setUser(null);
    setProfileComplete(false);
    setAuthStep(AUTH_STEPS.WELCOME);
    setAuthMode(AUTH_MODE.LOGIN);
    setSignupMethod(null);
    // Clear persisted signup profile
    try {
      sessionStorage.removeItem('bonndle:signup_profile');
    } catch (e) {
      // Ignore
    }
    setUserProfileState({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      country: 'NG',
      referralCode: '',
      boldTag: '',
    });
    setOtpValue(['', '', '', '']);
    setPinValue(['', '', '', '']);
    setError(null);
    setIsLoading(false);

    // Force a page reload to ensure fresh state
    window.location.href = '/';
  }, []);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await bonndleAuthApi.getProfile();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  }, []);

  /**
   * Update user profile on server
   */
  const updateProfile = useCallback(async (updates) => {
    console.log('[AuthContext] updateProfile called with:', updates);
    setIsLoading(true);
    clearError();
    try {
      // API expects camelCase for updates (firstName, lastName, dateOfBirth, gender)
      // but returns snake_case (first_name, last_name, date_of_birth)
      const apiUpdates = {};
      if (updates.firstName !== undefined) apiUpdates.firstName = updates.firstName;
      if (updates.lastName !== undefined) apiUpdates.lastName = updates.lastName;
      if (updates.email !== undefined) apiUpdates.email = updates.email;
      if (updates.dateOfBirth !== undefined) apiUpdates.dateOfBirth = updates.dateOfBirth;
      if (updates.gender !== undefined) apiUpdates.gender = updates.gender;
      // Note: country and phone are not in the expected PUT body per API docs

      console.log('[AuthContext] API payload:', apiUpdates);
      const userData = await bonndleAuthApi.updateProfile(apiUpdates);
      console.log('[AuthContext] updateProfile response:', userData);
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('[AuthContext] updateProfile error:', err);
      const message = err.message || 'Failed to update profile';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [clearError]);

  /**
   * Go back in auth flow
   */
  const goBack = useCallback(() => {
    clearError();
    switch (authStep) {
      case AUTH_STEPS.METHODS:
        setAuthStep(AUTH_STEPS.WELCOME);
        break;
      case AUTH_STEPS.DETAILS:
        setAuthStep(AUTH_STEPS.METHODS);
        break;
      case AUTH_STEPS.LOGIN_CONTACT:
        setAuthStep(AUTH_STEPS.WELCOME);
        break;
      case AUTH_STEPS.VERIFICATION:
        if (authMode === AUTH_MODE.SIGNUP) {
          setAuthStep(AUTH_STEPS.METHODS);
        } else {
          setAuthStep(AUTH_STEPS.LOGIN_CONTACT);
        }
        break;
      case AUTH_STEPS.PIN:
        setAuthStep(AUTH_STEPS.LOGIN_CONTACT);
        break;
      case AUTH_STEPS.PIN_CREATE:
        setAuthStep(AUTH_STEPS.VERIFICATION);
        break;
      default:
        break;
    }
  }, [authStep, authMode, clearError]);

  /**
   * Resend OTP
   */
  const resendOtp = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      // Use Firebase/Termii OTP service for phone (matches Flutter app)
      if (userProfile.phone && signupMethod === 'phone') {
        await otpService.resendOtp(userProfile.phone, 'recaptcha-container');
        console.log('[AuthContext] Phone OTP resent via OTP service');
      } else {
        // Use backend API for email OTP
        await bonndleAuthApi.sendOtp({
          email: userProfile.email || undefined,
          phone: userProfile.phone || undefined,
        });
      }
      return true;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, signupMethod, clearError]);

  const value = {
    // State
    isLoggedIn,
    isGuestMode,
    showGuestPrompt,
    user,
    profileComplete,
    isLoading,
    error,
    authMode,
    authStep,
    signupMethod,
    userProfile,
    otpValue,
    pinValue,
    AUTH_STEPS,
    AUTH_MODE,

    // Rewards state
    rewardsData,

    // Actions
    clearError,
    startOnboarding,
    checkContact,
    handleAuthMethod,
    handleGoogleSignIn,
    handleAppleSignIn,
    submitLoginContact,
    submitDetails,
    verifyAuth,
    createPin,
    confirmPin,
    forgotPin,
    resetPinWithOtp,
    enterApp,
    enterGuestMode,
    exitGuestMode,
    setShowGuestPrompt,
    completeProfile,
    logout,
    refreshUser,
    updateProfile,
    goBack,
    resendOtp,

    // Rewards actions
    fetchUserData,
    refreshRewards,
    silentRefreshBalance,

    // Setters
    setOtpValue,
    setPinValue,
    setUserProfile,
    setSignupMethod,
    setError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
